<template>
  <b-container fluid class="">
    <h1 class="m-3">
      {{ title }}
    </h1>
    <commonActions ref="commonAction" :data-ready="dataReadyTables" :filename-prefix="title"
      @commonFiltersUpdated="commonFiltersUpdated($event)" @clickDownloadPdf="downloadPdf($event)"
      @clickDownloadExcel="downloadExcel($event)" @clickDownloadCsv="downloadCsv($event)" />
    <table-renderer v-for="data of tableConfigs" :key="data.tableNumber" :table-header="data.tableHeader"
      :dimension-fields="data.dimensionColumnsTables" :measure-fields="data.measureColumnsTables"
      :data-table="data.dataTable" :data-ready="dataReadyTables" ref="tableRenderers" />
  </b-container>
</template>

<script>
import _ from "lodash";
import paths from "@/router/pathConstants";
import Tools from "@/utils/tools";
import TableTools from "@/utils/tableTools";
import TableRenderer from "@/components/TableRenderer.vue";
import commonActions from "@/components/pages/commonActions";
import mesureEnum from "@/model/mesureEnum";
import dimensionEnum from "@/model/dimensionEnum";
import { columnTypeEnum, calculatedColumnsEnum } from "@/model/columnsEnums";
import { formatAmount, formatPercentage } from "@/filter/numberFormat";
import { Cards } from "@/router/routeNames";
import { currencies } from "@/i18n/index.js";
import abortMixin from "@/mixins/abortMixin";

export default {
  name: Cards,
  mixins: [abortMixin],
  components: {
    TableRenderer,
    commonActions,
  },
  data() {
    return {
      siteValues: [],
      timeDimension: null,
      dataReadyTables: false,
      tableConfigs: [],
    }
  },
  computed: {
    title() {
      return this.$t("title.cards");
    },
  },
  methods: {
    commonFiltersUpdated(value) {
      this.siteValues = value.siteValues;
      this.timeDimension = value.timeDimension || null;
      this.updateTables(value.filters);
    },
    async updateTables(filters) {
      try {
        const { signal } = this.resetAbortController();
        this.dataReadyTables = false;

        /**
         * Configuration des tableaux pour chaque site sélectionné.
         * Permet d’afficher immédiatement les en-têtes des tableaux ainsi que le loader pour indiquer que les requêtes sont en cours.
         */
        this.tableConfigs = this.siteValues.map((site, i) => {
          const siteTable = {
            tableNumber: i.toString(),
            tableHeader: site.label,
            tableHeaderId: site.id,
            tableCurrency: site.currency,
            dimensionColumnsTables: [],
            measureColumnsTables: [],
            dataTable: [],
          };

          // Initialiser les colonnes immédiatement pour afficher le loader
          this.initColumnsTables(siteTable);

          return siteTable;
        });

        /** Requêtes CubeJS. */
        // Achats et recharges de cartes
        const mainQuery = {
          measures: [
            mesureEnum.PURCHASEHISTORY_COUNT,
            mesureEnum.PURCHASEHISTORY_AMOUNT,
            mesureEnum.PURCHASEHISTORY_TOKEN,
            mesureEnum.PURCHASEHISTORY_BONUSTOKEN,
          ],
          dimensions: [
            dimensionEnum.SITE,
            dimensionEnum.SITE_NAME,
            dimensionEnum.SITE_CURRENCY,
            dimensionEnum.PURCHASEHISTORY_NBCARDS,
            dimensionEnum.PURCHASEHISTORY_PRODUCT,
            dimensionEnum.PURCHASEHISTORY_CARDTRANSACTIONTYPE,
            dimensionEnum.PURCHASEHISTORY_ISBIRTHDAY,
          ],
          timeDimensions: this.timeDimension
            ? [{ ...this.timeDimension, dimension: dimensionEnum.PURCHASEHISTORY_DATETIME }]
            : [],
          filters: filters,
        };
        // Cartes recyclées
        const recyclingQuery = {
          measures: [mesureEnum.RECYCLING_COUNT],
          timeDimensions: this.timeDimension ? [this.timeDimension] : [],
          dimensions: [dimensionEnum.SITE, dimensionEnum.SITE_NAME],
          filters: filters,
        };
        // E-Réservations
        const onlineQuery = {
          measures: [
            mesureEnum.OMNIPLAYEVENT_NBCARDS,
            mesureEnum.OMNIPLAYEVENT_TOTALTTC,
          ],
          dimensions: [
            dimensionEnum.SITE,
            dimensionEnum.SITE_NAME,
          ],
          // À la demande du client, l’affichage des e-réservations se base sur la date de prestation plutôt que sur la date de réservation
          timeDimensions: this.timeDimension
            ? [{ ...this.timeDimension, dimension: dimensionEnum.OMNIPLAYEVENT_EVENTBUSINESSDATE }]
            : [],
          filters: filters,
        };

        let mainResult = await TableTools.Helper.doRequest(mainQuery, { signal });
        let recyclingResult = await TableTools.Helper.doRequest(recyclingQuery, { signal });
        let onlineResult = await TableTools.Helper.doRequest(onlineQuery, { signal });

        /**
         * Marquage des recyclages avec nbCard = -1,
         * Marquage des e-réservations avec IS_OMNIPLAY_EVENT = true,
         * Intégration des données dans l'objet principal.
         */
        recyclingResult.forEach((x) => {
          x[dimensionEnum.PURCHASEHISTORY_NBCARDS] = -1;
          x[dimensionEnum.PURCHASEHISTORY_ISBIRTHDAY] = false;
        });
        onlineResult.forEach((x) => {
          x[calculatedColumnsEnum.IS_OMNIPLAY_EVENT] = true
        })
        mainResult = [...mainResult, ...recyclingResult, ...onlineResult];

        /** Complétion des tableaux pour chaque site sélectionné. */
        this.tableConfigs.forEach(siteTable => {
          const siteId = siteTable.tableHeaderId;
          const siteData = mainResult.filter(r => r[dimensionEnum.SITE] === siteId);

          // Initialisation des colonnes en fonction des produits issus de la requête
          const columnsTables = this.initColumnsTables(siteTable, siteData);

          // Injection des données
          const finalObject = this.formatData(siteData, columnsTables, siteTable.tableCurrency);
          siteTable.dataTable = Object.keys(finalObject).map(key => {
            return {
              ...finalObject[key]
            }
          })
        });

        this.dataReadyTables = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        Tools.Alerts.Error(this.$swal, error);
      }
    },
    initColumnsTables(siteTable, siteData = []) {
      /** Dimension */
      const dimensionCols = [calculatedColumnsEnum.LABEL]
      siteTable.dimensionColumnsTables = TableTools.Helper.constructColumnTable(
        dimensionCols,
        null,
        columnTypeEnum.DIMENSION,
        false,
        null
      );

      /** Mesure */
      const measureCols = [
        calculatedColumnsEnum.CARDS,
        calculatedColumnsEnum.TOTAL_EURO,
        ...this.extractFetchedProducts(siteData, siteTable.tableCurrency),
        calculatedColumnsEnum.AVERAGE_BASKET
      ]
      siteTable.measureColumnsTables = TableTools.Helper.constructColumnTable(
        measureCols,
        null,
        columnTypeEnum.MEASURE,
        false,
        null,
        false,
        siteTable.tableCurrency
      );

      return [...dimensionCols, ...measureCols];
    },
    extractFetchedProducts(data, currency) {
      return _.chain(data)
        .map(r => Number(r[dimensionEnum.PURCHASEHISTORY_PRODUCT]))
        .filter(n => !isNaN(n))
        .uniq()
        .sortBy(n => n === 0 ? Infinity : n) // 0 va à la fin
        .map(p => p > 0
          ? this.getProductColumnName(p, currency)
          : calculatedColumnsEnum.OTHER_PRODUCTS)
        .value();
    },
    getProductColumnName(product, currency) {
      const currencySymbol = currencies[currency];
      return this.$t(dimensionEnum.PURCHASEHISTORY_PRODUCT).format(product, currencySymbol);
    },
    formatData(siteData, columnsTables, currency) {
      if (siteData.length === 0) {
        return [];
      }

      const { rowsTable, percentRows } = this.initRowsObjects(siteData, currency);

      let finalObject = rowsTable;

      const
        transacNbRow = finalObject[calculatedColumnsEnum.TRANSACTION_NUMBER],
        totalConsumedRow = finalObject[calculatedColumnsEnum.TOTAL_CONSUMED],
        monetaryRow = finalObject[calculatedColumnsEnum.MONETARY],
        cardsCol = calculatedColumnsEnum.CARDS,
        totalCol = calculatedColumnsEnum.TOTAL_EURO,
        otherProductsCol = calculatedColumnsEnum.OTHER_PRODUCTS,
        averageBasketCol = calculatedColumnsEnum.AVERAGE_BASKET;

      /********** Méthodes de calcul - Début **********/
      const handleSale = (cardsDealt, count, amount, productColumnName) => {
        const
          soldRow = finalObject[calculatedColumnsEnum.CARDS_SOLD],
          nbCards = cardsDealt * count;

        // Cartes vendues
        this.incrementValue(soldRow, cardsCol, nbCards, [transacNbRow, totalConsumedRow]);
        // Total TTC
        this.incrementValue(soldRow, totalCol, amount, [monetaryRow]);
        // Détail par produit
        this.incrementValue(soldRow, productColumnName, nbCards, [transacNbRow, monetaryRow], amount);
        // Panier moyen
        this.calculateBasketAverage(soldRow, totalCol, cardsCol);
      };

      const handleRefill = (refillType, count, amount, productColumnName) => {
        let rowKey = calculatedColumnsEnum.REFILL_NO_DETAIL;
        if (refillType === 'Recharge du jour') {
          rowKey = calculatedColumnsEnum.DAILY_REFILL;
        }
        if (refillType === 'Recharge ancienne') {
          rowKey = calculatedColumnsEnum.OLD_REFILL;
        }
        const refillRow = finalObject[rowKey];

        // Cartes rechargées
        this.incrementValue(refillRow, cardsCol, count, [transacNbRow]);
        // Total TTC
        this.incrementValue(refillRow, totalCol, amount, [monetaryRow]);
        // Détail par produit
        this.incrementValue(refillRow, productColumnName, count, [transacNbRow, monetaryRow], amount);
        // Panier moyen
        this.calculateBasketAverage(refillRow, totalCol, cardsCol);
      };

      const handleRecycling = (purchase) => {
        const
          recyclingRow = finalObject[calculatedColumnsEnum.RECYCLED],
          nbCards = purchase[mesureEnum.RECYCLING_COUNT] * (-1);

        // Cartes recyclées
        this.incrementValue(recyclingRow, cardsCol, nbCards, [totalConsumedRow])
      };

      const handleOnline = (nbCards, amount) => {
        const onlineRow = finalObject[calculatedColumnsEnum.ONLINE_BOOKING];

        // Cartes vendues
        this.incrementValue(onlineRow, cardsCol, nbCards, [transacNbRow, totalConsumedRow]);
        // Total TTC
        this.incrementValue(onlineRow, totalCol, amount, [monetaryRow]);
        // Panier moyen
        this.calculateBasketAverage(onlineRow, totalCol, cardsCol);
      };

      const handleQrCode = (cardsDealt, count, amount) => {
        const
          onlineRow = finalObject[calculatedColumnsEnum.QR_CODE],
          nbCards = cardsDealt * count;

        // Cartes vendues
        this.incrementValue(onlineRow, cardsCol, nbCards, [totalConsumedRow]);
        // Total TTC
        this.incrementValue(onlineRow, totalCol, amount, [monetaryRow]);
        // Panier moyen
        this.calculateBasketAverage(onlineRow, totalCol, cardsCol);
      };
      /********** Méthodes de calcul - Fin **********/

      /** Calcul des lignes */
      siteData.forEach(purchase => {
        const
          isOnlineBooking = purchase[calculatedColumnsEnum.IS_OMNIPLAY_EVENT],
          isQrCode = purchase[dimensionEnum.PURCHASEHISTORY_ISBIRTHDAY],
          cardsDealt = purchase[dimensionEnum.PURCHASEHISTORY_NBCARDS],
          refillType = purchase[dimensionEnum.PURCHASEHISTORY_CARDTRANSACTIONTYPE],
          count = purchase[mesureEnum.PURCHASEHISTORY_COUNT],
          amount = purchase[mesureEnum.PURCHASEHISTORY_AMOUNT],
          product = purchase[dimensionEnum.PURCHASEHISTORY_PRODUCT],
          productColumnName = product > 0
            ? this.getProductColumnName(product, currency)
            : otherProductsCol;

        if (isOnlineBooking) {
          /** E-réservation */
          handleOnline(
            purchase[mesureEnum.OMNIPLAYEVENT_NBCARDS],
            purchase[mesureEnum.OMNIPLAYEVENT_TOTALTTC]
          );
        } else if (isQrCode) {
          /** QR Code */
          handleQrCode(cardsDealt, count, amount);
        } else {
          /**
           * Tri des achats en fonction du nombre de cartes distribuées (PurchaseHistory.nbcardsasdimension) :
           *    - ACHAT : au moins une carte distribuée
           *    - RECHARGE : 0 carte distribuée
           *    - RECYCLAGE : -1 (valeur insérée artificiellement après la requête)
           */
          switch (true) {
            case cardsDealt > 0:
              handleSale(cardsDealt, count, amount, productColumnName);
              break;
            case cardsDealt === 0:
              handleRefill(refillType, count, amount, productColumnName);
              break;
            case cardsDealt === -1:
              handleRecycling(purchase);
              break;
            default:
              break;
          }
        }
      });

      /** Calcul des agrégats */
      let excludedColumns;
      const
        totalTransactions = transacNbRow[cardsCol],
        totalMonetary = monetaryRow[totalCol],
        totalOtherProducts = monetaryRow[otherProductsCol];

      // Panier moyen
      excludedColumns = new Set([
        calculatedColumnsEnum.LABEL,
        calculatedColumnsEnum.CARDS,
        calculatedColumnsEnum.TOTAL_EURO,
        calculatedColumnsEnum.OTHER_PRODUCTS,
        calculatedColumnsEnum.AVERAGE_BASKET,
      ])
      const { sum, count } = columnsTables.reduce(
        (acc, col) => {
          if (!excludedColumns.has(col)) {
            acc.sum += transacNbRow[col];
            acc.count++;
          }
          return acc;
        },
        { sum: 0, count: 0 }
      );

      transacNbRow[averageBasketCol] = count > 0
        ? Number((sum / count).toFixed(3))
        : 0;
      monetaryRow[averageBasketCol] = totalTransactions > 0
        ? totalMonetary / totalTransactions
        : 0;

      // % par produit
      excludedColumns = new Set([
        calculatedColumnsEnum.LABEL,
        calculatedColumnsEnum.TOTAL_EURO,
        calculatedColumnsEnum.AVERAGE_BASKET,
      ])
      const percentProductRow = finalObject[calculatedColumnsEnum.PERCENT_PRODUCT];
      columnsTables
        .filter(col => !excludedColumns.has(col))
        .forEach(col => {
          this.calcultatePercent(percentProductRow, col, transacNbRow[col], totalTransactions);
        });

      // % total valeur
      excludedColumns = new Set([
        calculatedColumnsEnum.LABEL,
        calculatedColumnsEnum.CARDS,
        calculatedColumnsEnum.AVERAGE_BASKET,
      ])
      const percentTotalValueRow = finalObject[calculatedColumnsEnum.PERCENT_TOTAL_VALUE];
      columnsTables
        .filter(col => !excludedColumns.has(col))
        .forEach(col => {
          this.calcultatePercent(percentTotalValueRow, col, monetaryRow[col], totalMonetary);
        });

      // Pourcentages de la dernière section du tableau
      excludedColumns = new Set([
        calculatedColumnsEnum.LABEL,
        calculatedColumnsEnum.AVERAGE_BASKET
      ])
      const usedColumns = columnsTables.filter(col => !excludedColumns.has(col));
      percentRows.forEach(({ row, percentRow }) => {
        // % recharges/vendues
        if (row !== calculatedColumnsEnum.ONLINE_BOOKING) {
          usedColumns.forEach(col => {
            if (col === cardsCol) {
              this.calcultatePercent(
                finalObject[percentRow],
                col,
                finalObject[row][col],
                totalTransactions
              );
            } else if (col === totalCol) {
              this.calcultatePercent(
                finalObject[percentRow],
                col,
                finalObject[row][col],
                totalMonetary
              );
            } else if (col === otherProductsCol) {
              const otherProductsRatio = transacNbRow[otherProductsCol]
                ? totalOtherProducts / transacNbRow[otherProductsCol]
                : 0;
              this.calcultatePercent(
                finalObject[percentRow],
                col,
                (finalObject[row][col] * otherProductsRatio),
                totalMonetary
              );
            } else {
              this.calcultatePercent(
                finalObject[percentRow],
                col,
                (finalObject[row][col] * parseFloat(col)),
                totalMonetary
              );
            }
          });
          // % e-réservations
        } else {
          const usedColumns = [
            calculatedColumnsEnum.CARDS,
            calculatedColumnsEnum.TOTAL_EURO
          ]
          usedColumns.forEach(col => {
            if (col === cardsCol) {
              this.calcultatePercent(
                finalObject[percentRow],
                col,
                finalObject[row][col],
                totalTransactions
              );
            } else if (col === totalCol) {
              this.calcultatePercent(
                finalObject[percentRow],
                col,
                finalObject[row][col],
                totalMonetary
              );
            }
          });
        }
      });

      /** Formatages */
      columnsTables.forEach(col => {
        if (Number.isFinite(monetaryRow[col])) {
          monetaryRow[col] = formatAmount(monetaryRow[col], false, currency);
        }
      });

      return finalObject;
    },
    initRowsObjects(siteData, currency) {
      let rowsTable = {};

      const displayedRows = this.displayingLines(siteData, true);

      const rows = [
        /**
         * Affichage dynamique des lignes en fonction des données.
         * Permet aussi de controller les lignes fixes.
         */
        ...(displayedRows.sold ? [{ name: calculatedColumnsEnum.CARDS_SOLD }] : []),
        ...(displayedRows.noDetailRefill ? [{ name: calculatedColumnsEnum.REFILL_NO_DETAIL }] : []),
        ...(displayedRows.oldRefill ? [{ name: calculatedColumnsEnum.OLD_REFILL }] : []),
        ...(displayedRows.dailyRefill ? [{ name: calculatedColumnsEnum.DAILY_REFILL }] : []),
        ...(displayedRows.online ? [{ name: calculatedColumnsEnum.ONLINE_BOOKING }] : []),
        { name: calculatedColumnsEnum.TRANSACTION_NUMBER, groupLevel: 1 }, // Ligne fixe
        { name: calculatedColumnsEnum.PERCENT_PRODUCT }, // Ligne fixe
        ...(displayedRows.recycling ? [{ name: calculatedColumnsEnum.RECYCLED }] : []),
        ...(displayedRows.qrCode ? [{ name: calculatedColumnsEnum.QR_CODE }] : []),
        { name: calculatedColumnsEnum.TOTAL_CONSUMED }, // Ligne fixe
        { name: calculatedColumnsEnum.MONETARY, groupLevel: 1 }, // Ligne fixe
        { name: calculatedColumnsEnum.PERCENT_TOTAL_VALUE }, // Ligne fixe
        ...(displayedRows.sold ? [{ name: calculatedColumnsEnum.PERCENT_CARDS_SOLD }] : []),
        ...(displayedRows.noDetailRefill ? [{ name: calculatedColumnsEnum.PERCENT_REFILL_NO_DETAIL }] : []),
        ...(displayedRows.oldRefill ? [{ name: calculatedColumnsEnum.PERCENT_OLD_REFILL }] : []),
        ...(displayedRows.dailyRefill ? [{ name: calculatedColumnsEnum.PERCENT_DAILY_REFILL }] : []),
        ...(displayedRows.online ? [{ name: calculatedColumnsEnum.PERCENT_ONLINE_BOOKING }] : []),
      ];

      rows.forEach(r => {
        rowsTable[r.name] = {
          [calculatedColumnsEnum.LABEL]: this.$t(r.name),
          [dimensionEnum.SITE_CURRENCY]: currency,
          groupLevel: r.groupLevel,
        }
      });

      const percentMapping = {
        [calculatedColumnsEnum.DAILY_REFILL]: calculatedColumnsEnum.PERCENT_DAILY_REFILL,
        [calculatedColumnsEnum.OLD_REFILL]: calculatedColumnsEnum.PERCENT_OLD_REFILL,
        [calculatedColumnsEnum.REFILL_NO_DETAIL]: calculatedColumnsEnum.PERCENT_REFILL_NO_DETAIL,
        [calculatedColumnsEnum.CARDS_SOLD]: calculatedColumnsEnum.PERCENT_CARDS_SOLD,
        [calculatedColumnsEnum.ONLINE_BOOKING]: calculatedColumnsEnum.PERCENT_ONLINE_BOOKING,
      };
      const percentRows = Object.entries(percentMapping)
        .filter(([row]) => rowsTable[row])
        .map(([row, percentRow]) => ({ row, percentRow }));

      return { rowsTable, percentRows };
    },
    displayingLines(siteData, allLinesDisplayed = false) {
      const result = {
        dailyRefill: false,
        oldRefill: false,
        noDetailRefill: false,
        sold: false,
        online: false,
        recycling: false,
        qrCode: false,
      };

      if (allLinesDisplayed) {
        /**
         * Affiche toutes les lignes, peu importe s’il y a des données.
         * Sauf pour la ligne 'Recharge' qui demeure dynamique,
         * car c'est une ligne back-up pour les recharges qui n'ont pas de PurchaseHistoryState affiliées.
         */
        const hasNoDetailRefill = siteData.some(
          x =>
            x[dimensionEnum.PURCHASEHISTORY_CARDTRANSACTIONTYPE] == null &&
            x[dimensionEnum.PURCHASEHISTORY_NBCARDS] === 0
        );
        Object.keys(result).forEach(key => {
          result[key] = key === 'noDetailRefill' ? hasNoDetailRefill : true;
        });

        return result
      }

      for (const item of siteData) {
        const transactionType = item[dimensionEnum.PURCHASEHISTORY_CARDTRANSACTIONTYPE];
        const nbCards = item[dimensionEnum.PURCHASEHISTORY_NBCARDS];
        const isQrCode = item[dimensionEnum.PURCHASEHISTORY_ISBIRTHDAY];
        const isOnlineBooking = item[calculatedColumnsEnum.IS_OMNIPLAY_EVENT];

        if (transactionType === 'Recharge du jour') result.dailyRefill = true;
        if (transactionType === 'Recharge ancienne') result.oldRefill = true;
        if (nbCards === 0 && transactionType == null) result.noDetailRefill = true;
        if (nbCards > 0 && !isQrCode) result.sold = true;
        if (isOnlineBooking) result.online = true;
        if (nbCards === -1) result.recycling = true;
        if (isQrCode) result.qrCode = true;

        // Early exit si toutes les conditions sont remplies
        if (result.dailyRefill && result.oldRefill && result.noDetailRefill &&
          result.sold && result.online && result.recycling && result.qrCode) {
          break;
        }
      }

      return result;
    },
    incrementValue(obj, key, value, incrementLines = [], optionalAmount = null) {
      const safeValue = Number(value) || 0;
      obj[key] = (Number(obj[key]) || 0) + safeValue;
      // Incrémentation des lignes : Nombre de transactions, Total consommées, Monétaire
      incrementLines.forEach(line => {
        const amount = line[calculatedColumnsEnum.LABEL] === 'Monétaire' && key !== calculatedColumnsEnum.TOTAL_EURO
          ? Number(optionalAmount) || 0
          : safeValue;
        line[key] = (Number(line[key]) || 0) + amount;
      });
    },
    calcultatePercent(obj, key, numerator, denominator) {
      if (denominator) {
        obj[key] = formatPercentage((numerator / denominator) * 100);
      }
    },
    calculateBasketAverage(obj, dividend, divisor) {
      obj[calculatedColumnsEnum.AVERAGE_BASKET] = obj[divisor]
        ? obj[dividend] / obj[divisor]
        : 0;
    },

    /** EXPORT */
    downloadPdf(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData(filename, subtitles) {
      let datasForExport = [];

      this.tableConfigs.forEach((table, index) => {
        const renderer = this.$refs.tableRenderers?.[index];
        const sortKey = renderer?.sortedColumn || table.defaultSortedColumn;

        const data = TableTools.Helper.assembleExportDataTable(
          table.dataTable,
          table.dimensionColumnsTables,
          table.measureColumnsTables,
          null,
          table.tableHeader,
          sortKey,
          null,
          null,
          null,
          null,
          null
        );
        datasForExport.push(data);
      });

      TableTools.Helper.storeExportData(
        datasForExport,
        this.title,
        subtitles,
        filename
      );
    },
  }
}
</script>
